import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';

interface Zone {
  id: string;
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  occupancy: number[];
  capacity: number;
}

interface FloorPlanHeatmapProps {
  zones: Zone[];
  currentHour?: number;
  onHourChange?: (hour: number) => void;
  showScrubber?: boolean;
  width?: number;
  height?: number;
  mode?: 'heatmap' | 'availability';
  bookedZones?: string[];
}

const FloorPlanHeatmap: React.FC<FloorPlanHeatmapProps> = ({
  zones,
  currentHour = 9,
  onHourChange,
  showScrubber = true,
  width = 800,
  height = 500,
  mode = 'heatmap',
  bookedZones = []
}) => {
  const [hour, setHour] = useState(currentHour);
  const [isPlaying, setIsPlaying] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (onHourChange) {
      onHourChange(hour);
    }
  }, [hour, onHourChange]);

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      setHour(prev => {
        if (prev >= 18) {
          setIsPlaying(false);
          return 8;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(interval);
  }, [isPlaying]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, width, height);

    ctx.fillStyle = '#F9F7F2';
    ctx.fillRect(0, 0, width, height);

    zones.forEach(zone => {
      const hourIndex = hour - 8;
      const occupancy = zone.occupancy[hourIndex] || 0;
      const utilizationPercent = (occupancy / zone.capacity) * 100;

      if (mode === 'availability') {
        const isBooked = bookedZones.includes(zone.id);
        const hasPresence = occupancy > 0;

        if (isBooked && !hasPresence) {
          ctx.fillStyle = '#fee';
        } else if (hasPresence) {
          ctx.fillStyle = '#0A9B9B33';
        } else {
          ctx.fillStyle = '#F9F7F2';
        }
      } else {
        if (utilizationPercent === 0) {
          ctx.fillStyle = '#F9F7F2';
        } else if (utilizationPercent < 25) {
          ctx.fillStyle = '#0A9B9B22';
        } else if (utilizationPercent < 50) {
          ctx.fillStyle = '#0A9B9B44';
        } else if (utilizationPercent < 75) {
          ctx.fillStyle = '#0A9B9B88';
        } else {
          ctx.fillStyle = '#0A9B9B';
        }
      }

      ctx.fillRect(zone.x, zone.y, zone.width, zone.height);

      ctx.strokeStyle = '#1a1a1a';
      ctx.lineWidth = 1;
      ctx.strokeRect(zone.x, zone.y, zone.width, zone.height);

      if (occupancy > 0 && mode === 'heatmap') {
        ctx.fillStyle = '#1a1a1a';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(`${occupancy}`, zone.x + zone.width / 2, zone.y + zone.height / 2 + 4);
      }
    });
  }, [zones, hour, width, height, mode, bookedZones]);

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newHour = parseInt(e.target.value);
    setHour(newHour);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (hour >= 18) {
      setHour(8);
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          border: '1px solid #e8e4dc',
          borderRadius: '8px',
          backgroundColor: '#F9F7F2'
        }}
      />

      {showScrubber && (
        <div className="mt-4">
          <div className="flex items-center gap-4">
            <button
              onClick={togglePlay}
              className="p-2 rounded-lg transition-colors"
              style={{
                backgroundColor: 'var(--sca-teal)',
                color: 'white',
                border: 'none'
              }}
            >
              {isPlaying ? <Pause size={16} /> : <Play size={16} />}
            </button>

            <div className="flex-1">
              <input
                type="range"
                min="8"
                max="18"
                value={hour}
                onChange={handleScrub}
                className="w-full"
                style={{ accentColor: 'var(--sca-teal)' }}
              />
            </div>

            <div className="text-sm font-medium" style={{ color: 'var(--sca-ink)', minWidth: '80px' }}>
              {hour}:00 {hour < 12 ? 'AM' : 'PM'}
            </div>
          </div>

          <div className="flex justify-between text-xs mt-2" style={{ color: 'var(--sca-muted)' }}>
            <span>8 AM</span>
            <span>6 PM</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FloorPlanHeatmap;
